<template>
  <section class="print-students">
    <portal to="printcard">
      <v-card flat class="print-cards">
        <div
          v-for="(s, index) in printList"
          :key="index"
          :class="['card-wrapper', 'mt-3', 'px-2', { pageBreak: index % 8 }]"
        >
          <student-card
            :name="s.name"
            :class-code="s.invitation_code"
            :class-name="currentClass.name"
          />
        </div>
      </v-card>
    </portal>
    <v-row row justify-center wrap class="mb-2">
      <v-col cols="12">
        <h2 style="line-height: 48px;">Print Student Cards</h2>
      </v-col>
      <v-col md="6">
        <p>
          You can help your students complete the enrolment process by printing
          their Student Cards and distributing them to the class. These Student
          Cards have information on how to enrol into the class you have
          created, the class name, and their unique student code. When printing
          out these Student Cards please note that 8 Student Cards will be
          printed on a single A4 page.
          <br /><br />
          We recommend also downloading the Excel version of the student list in
          the ‘Download List’ tab above so you can easily keep track of which
          student codes apply to each of your students.
        </p>
      </v-col>
      <v-col md="6">
        <div class="card-container">
          <student-card class-code="####" class-name="Red 10" />
        </div>
        <h5 class="title card-note text-right">
          Student card example above
        </h5>
      </v-col>
      <v-col cols="12">
        <v-card flat>
          <v-card-actions>
            <v-btn @click="print" color="primary" class="mr-3">
              <v-icon left dark>print</v-icon>
              {{ selected.length ? 'Print selected cards' : 'Print cards' }}
            </v-btn>
            <v-select
              v-model="filter"
              :items="filterOptions"
              label="Filter"
              class="print-filter"
            ></v-select>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              append-icon="search"
              label="Search"
              single-line
              hide-details
              class="pb-3"
            ></v-text-field>
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-data-table
          v-model="selected"
          :headers="headers"
          :items="classInvitations"
          :search="search"
          select-show
          item-key="id"
          hide-default-footer
          :loading="loading"
          :options.sync="pagination"
          show-select
          flat
          @page-count="pageCount = $event"
        >
          <template v-slot:no-data>
            <p class="mb-0 text-center">
              Loading, please wait
            </p>
          </template>
          <template v-slot:no-results>
            <p class="mb-0 text-center">
              No students in selection
            </p>
          </template>
          <template v-slot:body="{ items, isSelected, select }">
            <tbody>
              <tr
                v-for="item in items"
                :key="item.id"
                :active="isSelected(item)"
                @click.stop="select(item, !isSelected(item))"
              >
                <td>
                  <v-checkbox
                    color="primary"
                    :input-value="isSelected(item)"
                    primary
                    hide-details
                    class="pa-0 ma-0"
                    @click.stop="select(item, !isSelected(item))"
                  ></v-checkbox>
                </td>
                <td class="text-center">
                  {{ item.invitation_code }}
                </td>
                <td class="text-center">
                  {{ item.accepted_at || 'Not accepted' }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
        <div v-show="pageCount > 1" class="text-center pt-2">
          <v-pagination
            v-model="pagination.page"
            :length="pageCount"
          ></v-pagination>
        </div>
      </v-col>
    </v-row>
  </section>
</template>

<script>
import { focus } from 'vue-focus'
import StudentCard from './StudentCard'
import { mapGetters } from 'vuex'
import ClassInvitation from '@/modules/class/models/ClassInvitation'
import Class from '@/modules/class/models/Class'

export default {
  name: 'PrintStudents',
  directives: { focus: focus },
  components: {
    StudentCard
  },
  data() {
    return {
      pageCount: 0,
      pagination: {
        itemsPerPage: 10,
        sortBy: ['name'],
        sortDesc: [true]
      },
      filter: 'not-accepted',
      filterOptions: [
        { text: '- All -', value: 'all' },
        { text: 'Students who accepted invites', value: 'accepted' },
        { text: "Students who haven't accepted invites", value: 'not-accepted' }
      ],
      headers: [
        { text: 'Student Code', value: 'invitation_code', align: 'center' },
        { text: 'Invitation accepted', value: 'accepted_at', align: 'center' }
      ],
      selected: [],
      search: '',
      loading: false
    }
  },
  computed: {
    ...mapGetters('entities/classes', ['currentClassId']),
    currentClass() {
      return Class.find(this.currentClassId)
    },
    printList() {
      if (this.selected.length) {
        return this.selected
      }
      return this.classInvitations
    },
    classInvitations() {
      let q = ClassInvitation.query().where(
        'custom_class_id',
        this.currentClassId
      )

      if (this.filter === 'accepted') {
        q.where(record => record.accepted_at !== null)
      } else if (this.filter === 'not-accepted') {
        q.where('accepted_at', null)
      }

      return q.get()
    }
  },
  async mounted() {
    this.loading = true
    await ClassInvitation.dispatch('getClassInvitations', {
      classId: this.currentClassId
    })
    this.loading = false
  },
  methods: {
    toggleAll() {
      if (this.selected.length) this.selected = []
      else this.selected = this.classInvitations.slice()
    },
    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending
      } else {
        this.pagination.sortBy = column
        this.pagination.descending = false
      }
    },
    columnValueList(val) {
      return this.desserts.map(d => d[val])
    },
    print() {
      document.body.classList.add('printcards')
      window.print()
      document.body.classList.remove('printcards')
    }
  }
}
</script>

<style lang="scss">
@media print {
  body.printcards {
    .main-content {
      display: none;
    }

    .print-cards {
      display: block !important;
      -webkit-print-color-adjust: exact;
      .card-wrapper {
        position: relative;
        -webkit-column-break-inside: avoid;
        page-break-inside: avoid;
        break-inside: avoid;
      }
      .v-image__image {
        -webkit-print-color-adjust: exact;
        background-image: url('http://localhost:8080/img/Logo_Yellow_White.d223ee3e.png');
      }
    }
  }
}
.print-cards {
  display: none;
  .card-wrapper {
    width: 50%;
    display: inline-block;
    vertical-align: middle;
  }
}

.print-students {
  .v-datatable__actions__range-controls {
    display: none !important;
  }

  h2 {
    color: var(--v-primary-base);
    width: 100%;
  }

  .card-note {
    font-size: 12px;
    line-height: 45px;
    clear: both;
  }

  .print-filter {
    max-width: 340px;
  }

  .card-container {
    float: right;
    width: 380px;

    &:after {
      display: block;
      content: '';
      clear: both;
    }
  }
}
</style>
